import React, { memo } from 'react';

import { createPortal } from 'react-dom';

import { ProductKeys } from '../../../common/constants/products';
import { usePlacementPortalContainer } from '../../../common/utils/post-office-placement/placement-portal-context';
import { QuickFindBannerPlacement } from '../../../common/utils/post-office-placement/quick-find-banner-placement';
import { useBannerEligibleProductsOrder } from '../../3p-smart-link-usage';
import { useOAuthHandlers } from '../../oauth';
import {
	useAvailableProducts,
	usePrimaryProduct,
	useThirdPartyConfigs,
} from '../../store/bootstrap';

type QuickFindBannerPortalProps = {
	onBannerMessageViewed?: (trackImpression: () => void) => void;
	isInteracted?: boolean;
};

export const QuickFindBannerPortal: React.MemoExoticComponent<
	(props: QuickFindBannerPortalProps) => React.ReactPortal | null
> = memo(
	({
		onBannerMessageViewed,
		isInteracted,
	}: QuickFindBannerPortalProps): React.ReactPortal | null => {
		const portalContainer = usePlacementPortalContainer();
		const [thirdPartyConfigs] = useThirdPartyConfigs();
		const availableProducts = useAvailableProducts();
		const [primaryProduct] = usePrimaryProduct();
		// In Jira, the quick find component is already rendered on hover, so isInteracted is not needed.
		const isJira = primaryProduct === ProductKeys.Jira;

		const bannerEligibleProductsOrder = useBannerEligibleProductsOrder({
			isOnSearchDialog: true,
			thirdPartyConfigs,
			availableProducts,
		});

		const hasBannerEligibleProducts = bannerEligibleProductsOrder.length > 0;

		if (
			!hasBannerEligibleProducts ||
			!portalContainer ||
			(!isInteracted && !isJira)
		) {
			return null;
		}

		// Render the Quick Find banner placement into the dialog's portal container so the
		// placement is mounted when results are shown, without changing the main results DOM.
		return createPortal(
			<QuickFindBannerPlacement
				thirdPartyConfigs={thirdPartyConfigs}
				availableProductsOrder={bannerEligibleProductsOrder}
				useOAuthHandlers={useOAuthHandlers}
				onMessageViewed={onBannerMessageViewed}
			/>,
			portalContainer,
		);
	},
);
